import * as angular from 'angular';
import { includes, inRange } from 'lodash';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { QueryOptions } from '@mode/shared/data-access-algolia';
import { MixableBaseController } from '~/common/component';
import { BugsnagHelpers } from '~/lib/bugsnag-helpers';
import { SelectOption } from '@mode/shared/ui-angularjs';
import { TableListData } from '@mode/shared/ui-angularjs';
import { TableListSchema } from '@mode/shared/ui-angularjs';
import { TableListValueType } from '@mode/shared/ui-angularjs';
import { SearchClientService } from '@mode/algolia/domain';
import { getAppName, getThoughtspotIntegrated } from '@mode/shared/util-js';

// configure QueryOptions and constants
const DEFAULT_PAGE = 0;
const DEFAULT_RESULT_PAGE_SIZE = 19;
type GroupMembersQueryOptions = QueryOptions<'user'> & {
  rawResults?: true;
};

export class GroupMembersBrowserController extends MixableBaseController {
  readonly appName = getAppName();
  private canEditGroup: boolean;
  private onMemberRemoved: () => void;
  private dropdownEnabled: boolean;
  private dropdownTooltipMessage: string;
  private dropdownTooltipEnabled: boolean;
  private orgToken: string;
  private groupToken: string;
  private groupMembersActionOptionsHash$: Observable<{ [token: string]: any }>;
  private groupMembersActionOptionsHash: any;
  private groupMemberships: any[];
  private filteredMembershipTokens: string[];
  private groupMembers$: BehaviorSubject<TableListData<any>[]> = new BehaviorSubject([]);
  private query$: BehaviorSubject<string> = new BehaviorSubject(null);
  private pageNum$: BehaviorSubject<number> = new BehaviorSubject(DEFAULT_PAGE);
  private currPage$: BehaviorSubject<number> = new BehaviorSubject(null);
  private pageCount$: BehaviorSubject<number> = new BehaviorSubject(0);
  private hitsCount$: BehaviorSubject<number> = new BehaviorSubject(0);
  private groupMembersTableListSchema: TableListSchema = {
    fields: [
      {
        name: 'avatar',
        title: 'Member Name',
        type: TableListValueType.Avatar,
      },
      {
        name: 'name',
        title: null,
        type: TableListValueType.Html,
        tooltip: (cell) => cell.name,
      },
      {
        name: 'username',
        title: 'Username',
        type: TableListValueType.Text,
        tooltip: (cell) => cell.username,
      },
    ],
  };

  private isLoading = true;
  private thoughtspotIntegrated: boolean;

  static $inject = ['$window', 'SearchClientService', 'ToastService'];

  constructor(
    private $window: angular.IWindowService,
    private _searchClientService: SearchClientService,
    private AlertService
  ) {
    super();

    this.thoughtspotIntegrated = getThoughtspotIntegrated(this.$window.document);
    this.groupMembersActionOptionsHash$ = this.groupMembers$.pipe(
      map((members) =>
        members.reduce(
          (hash, member) => ({
            ...hash,
            ...{
              [member.values.token]: [
                {
                  label: 'Remove Member',
                  value: `[GroupMembership] Remove`,
                  isValid: () => !this.thoughtspotIntegrated,
                },
              ].filter((o) => o.isValid()),
            },
          }),
          {}
        )
      )
    );

    this.groupMembersActionOptionsHash$.pipe(takeUntil(this.destroyed$)).subscribe((h) => {
      this.groupMembersActionOptionsHash = h;
    });

    const queries$ = this.query$.pipe(filter((query) => query != null));
    combineLatest(queries$, this.pageNum$).subscribe(([query, pageNum]: [string, number]) => {
      this.queryGroups(query, pageNum);
    });
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.groupMemberships) {
      const incomingMembershipTokens = changes.groupMemberships.currentValue.map(
        (groupMember) => groupMember.member_token
      );
      this.filteredMembershipTokens = incomingMembershipTokens;
      this.updateQuery();
    }
    if (changes.canEditGroup) {
      const canEdit: boolean = changes.canEditGroup.currentValue;
      this.dropdownEnabled = canEdit === true;
    }
    this.dropdownTooltipMessage = `This Group is managed through SCIM and cannot be modified in ${this.appName}`;
  }

  //
  // Querying to Algolia and parsing its results
  queryGroups(query = '', page = DEFAULT_PAGE) {
    if (!this.orgToken || !this.groupToken) {
      // if the orgToken or groupToken is not set,
      // do not run a query
      return;
    }

    const opts: GroupMembersQueryOptions = {
      page: page,
      query: query || '',
      indexes: ['user'],
      perPage: DEFAULT_RESULT_PAGE_SIZE,
      rawResults: true,
      filters: `organization_${this.orgToken},group_${this.groupToken}`,
    };

    const queryPromise = this._searchClientService.query(opts);

    return queryPromise
      .then((response) => this.handleUserGroupMembersSearchResponse(response))
      .catch((err) => BugsnagHelpers.notifySafely(err));
  }

  handleUserGroupMembersSearchResponse(response): void {
    const userHits = (response) => response.results[0].hits;
    const currPage = (response) => response.results[0].page;
    const pageCount = (response) => response.results[0].nbPages;
    const hitsCount = (response) => response.results[0].nbHits;
    const groupMembersList = userHits(response)
      .map((member) => this._searchClientService.createGroupMemberFromHit(member))
      .filter((member) => this.filteredMembershipTokens.includes(member.token))
      .map((member) => ({
        values: member,
      }));

    this.isLoading = false;
    this.groupMembers$.next(groupMembersList);
    this.currPage$.next(currPage(response));
    this.pageCount$.next(pageCount(response));
    this.hitsCount$.next(hitsCount(response));
  }

  //
  // Search & Filtering Logic
  handleQueryUpdate($query: string): void {
    this.updateQuery($query);
  }

  updateQuery(query = '') {
    this.resetPagination();
    this.query$.next(query);
  }

  //
  // Pagination Logic
  updatePage(pageNum: number) {
    this.pageNum$.next(pageNum);
  }

  validPage(page: number, pageRange: number): boolean {
    return inRange(page, 0, pageRange + 1);
  }

  handlePageSelection($page: number) {
    if (this.validPage($page, this.pageCount$.getValue())) {
      this.updatePage($page);
    }
  }

  resetPagination(): void {
    this.currPage$.next(0);
    this.pageNum$.next(0);
  }

  //
  // Actions For browser items
  getActionOptions(rowIndex: number): any[] {
    const currentHash = this.groupMembersActionOptionsHash;
    return currentHash[this.getTokenFromIndex(rowIndex)];
  }

  getTokenFromIndex(index: number) {
    const groupMembers = this.groupMembers$.getValue();
    const targetToken = groupMembers[index] ? groupMembers[index].values.token : undefined;
    return targetToken;
  }

  handleMemberRemovedSuccess(targetMemberToken) {
    this.AlertService.success('Removed user'); // alert the user of a successfull removal
  }

  handleMemberRemovedFailure(err) {
    this.AlertService.error('Could not remove user');
    BugsnagHelpers.notifySafely(err);
  }

  handleRowActionOptionSelection($option: SelectOption, $rowIndex: number) {
    const groupMembersOnPage = this.groupMembers$.getValue();
    const targetMemberToken = groupMembersOnPage[$rowIndex].values.token;
    const targetMemberModel = this.groupMemberships.find((mbrObj) => mbrObj.member_token == targetMemberToken);

    // if the action is to remove
    if ($option.value && includes($option.value.toString().toLowerCase(), 'remove')) {
      // use the destroy method on the model obj
      targetMemberModel
        .destroy()
        .then(() => {
          this.handleMemberRemovedSuccess(targetMemberToken);
        })
        .catch((e) => this.handleMemberRemovedFailure(e));
    }
  }
}

export const GroupMembersBrowserComponent: angular.IComponentOptions = {
  controller: GroupMembersBrowserController,
  controllerAs: 'ctrl',
  bindings: {
    orgToken: '<',
    groupToken: '<',
    canEditGroup: '<',
    groupMemberships: '<',
    dropdownTooltipEnabled: '<',
    onMemberRemoved: '&',
  },
  template: `
    <browser ng-if="!ctrl.isLoading">
      <browser-header>
        <capra-search-input placeholder="Search..." (text-change)="ctrl.handleQueryUpdate($event)" />
      </browser-header>

      <browser-body>
        <table-list
          ng-if="(ctrl.groupMembers$ | async:this).length"
          schema="ctrl.groupMembersTableListSchema"
          data="ctrl.groupMembers$ | async:this"
          dropdown-enabled="ctrl.dropdownEnabled"
          dropdown-tooltip-message="ctrl.dropdownTooltipMessage"
          dropdown-tooltip-enabled="ctrl.dropdownTooltipEnabled"
          on-row-dropdown-init="ctrl.getActionOptions($index)"
          on-row-dropdown-select="ctrl.handleRowActionOptionSelection($option, $index)"
        />

        <div ng-if="!(ctrl.groupMembers$ | async:this).length">
          No Results Found
        </div>
      </browser-body>

      <browser-footer>
        <paginator
          curr-page="ctrl.currPage$ | async:this"
          page-range="ctrl.pageCount$ | async:this"
          inner-window-size="2"
          left-outer-window-size="1"
          right-outer-window-size="1"
          on-page-selection="ctrl.handlePageSelection($pageIndex)"
          num-items-page="(ctrl.groupMembers$ | async:this).length"
          num-items-total="ctrl.hitsCount$ | async:this" />
      </browser-footer>
    </browser>

    <div ng-if="ctrl.isLoading">
      <mode-loader></mode-loader>
    </div>
  `,
};
